import React, { useCallback } from "react"

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { GripVertical, Trash2 } from "lucide-react"

import type { FilterConfig } from "@/types/metrics/metadata"

import {
  createDefaultRangeFilter,
  createDefaultRoleFilter,
  FILTER_TYPES,
} from "../types"
import { RangeFilterEditor } from "./RangeFilterEditor"
import { RoleFilterEditor } from "./RoleFilterEditor"

type TranscriptFilterType = "range" | "role"

interface FilterCardProps {
  filter: FilterConfig
  onChange: (filter: FilterConfig) => void
  onRemove: () => void
}

export const FilterCard: React.FC<FilterCardProps> = ({
  filter,
  onChange,
  onRemove,
}) => {
  const handleTypeChange = useCallback(
    (value: string) => {
      const newType = value as TranscriptFilterType
      if (newType === filter.type) {
        return
      }
      onChange(
        newType === "range"
          ? createDefaultRangeFilter()
          : createDefaultRoleFilter(),
      )
    },
    [filter.type, onChange],
  )

  return (
    <div className="flex items-center gap-2">
      <GripVertical
        className="w-4 h-4 text-coval-text-muted cursor-grab active:cursor-grabbing shrink-0"
        aria-hidden
      />
      <div className="flex-1 flex items-center gap-3">
        <Select value={filter.type} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-32" aria-label="Filter type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTER_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1">
          {filter.type === "range" ? (
            <RangeFilterEditor filter={filter} onChange={onChange} />
          ) : (
            <RoleFilterEditor filter={filter} onChange={onChange} />
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        icon
        onClick={onRemove}
        tooltip="Remove filter"
        className="text-coval-text-muted hover:text-coval-danger-text"
      >
        <Trash2 />
      </Button>
    </div>
  )
}
